import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const ImageLightbox = () => {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState("");
  const [alt, setAlt] = useState("");
  const [allImages, setAllImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const collectImages = useCallback(() => {
    const imgs = Array.from(document.querySelectorAll("main img"));
    return imgs.map((img) => ({ src: img.src, alt: img.alt || "" }));
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      const img = e.target.closest("main img");
      if (!img) return;
      // Skip if the image is inside an interactive element (button/link/card action)
      if (img.closest('button, a, [role="button"]')) return;
      e.preventDefault();
      e.stopPropagation();

      const images = collectImages();
      const idx = images.findIndex((i) => i.src === img.src);
      setAllImages(images);
      setCurrentIndex(idx >= 0 ? idx : 0);
      setSrc(img.src);
      setAlt(img.alt || "");
      setOpen(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [collectImages]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  });

  const goNext = () => {
    if (allImages.length === 0) return;
    const next = (currentIndex + 1) % allImages.length;
    setCurrentIndex(next);
    setSrc(allImages[next].src);
    setAlt(allImages[next].alt);
  };

  const goPrev = () => {
    if (allImages.length === 0) return;
    const prev = (currentIndex - 1 + allImages.length) % allImages.length;
    setCurrentIndex(prev);
    setSrc(allImages[prev].src);
    setAlt(allImages[prev].alt);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
      onClick={() => setOpen(false)}
      data-testid="image-lightbox"
    >
      {/* Close button */}
      <button
        onClick={() => setOpen(false)}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Counter */}
      {allImages.length > 1 && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-xs font-medium z-10">
          {currentIndex + 1} / {allImages.length}
        </div>
      )}

      {/* Prev button */}
      {allImages.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Next button */}
      {allImages.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Image */}
      <img
        src={src}
        alt={alt}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />

      {/* Alt text */}
      {alt && (
        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium max-w-md text-center">
          {alt}
        </p>
      )}
    </div>
  );
};
